// Shared "SLA" component — the SLA Reports panel used by BOTH the dashboard SPA
// and the embeddable widget. Presentational only: each host injects an `SLAClient`
// (the auth/transport seam — header-auth for the widget, bearer JWT for the SPA)
// and an `onAskWhy` nav callback, and loads the matching styles
// (`@netwatchai/chat-core/sla.css`; the widget inlines it into its shadow DOM).
// Same shared-component pattern as `Incidents` and `Outlook`.
//
// Three sub-tabs: "Latest report" (perms-scoped summary + per-host status, with
// a CSV download and "Ask why" deep-links into chat), "Schedules" (client-wide
// CRUD with All/Mine, a searchable group picker, multi-select cadence, recipients,
// and send-now), and "Settings" (breach threshold + weekend/maintenance
// exclusions — writes the same per-client row the dashboard settings page does).
// Schedules + Settings are ADMIN-ONLY (`GET /sla/me`): non-admins see just the
// Latest report — no sub-tab bar at all.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { EmptyState } from './EmptyState'
import { humanizeSeconds } from './duration'
import { useOutsidePress } from './useOutsidePress'

const PERIODS: [string, string][] = [
  ['today', 'Today'], ['this_week', 'This week'], ['this_month', 'This month'],
  ['this_quarter', 'This quarter'], ['ytd', 'Year to date'],
]
const OS_OPTS: [string, string][] = [['all', 'All'], ['windows', 'Windows'], ['linux', 'Linux'], ['ip_device', 'Network']]
// What a schedule's email carries. The PDF is the designed report people
// forward; the CSV is the one they open in a spreadsheet (docs/sla-pdf.md).
const ATTACHMENT_OPTS: [string, string][] = [['csv', 'CSV'], ['pdf', 'PDF'], ['both', 'Both']]
// The same choice, said in passing on a schedule card.
const ATTACHMENT_SUB: Record<string, string> = {
  csv: 'CSV attached', pdf: 'PDF attached', both: 'PDF + CSV attached',
}
const ATTACHMENT_HINT: Record<string, string> = {
  csv: 'A spreadsheet of every host — the classic export.',
  pdf: 'The designed report, in the dashboard’s look — the one to forward.',
  both: 'The designed PDF to read, plus the CSV to work with.',
}
const CADENCES: [string, string][] = [['daily', 'D'], ['weekly', 'W'], ['monthly', 'M'], ['quarterly', 'Q'], ['yearly', 'Y']]
const CADENCE_LABEL: Record<string, string> = { daily: 'DAILY', weekly: 'WEEKLY', monthly: 'MONTHLY', quarterly: 'QUARTERLY', yearly: 'YEARLY' }
const SEND_HOURS = Array.from({ length: 24 }, (_, h) => h)
// '' = auto: the report covers the period matching the cadence.
const PERIOD_OPTS: [string, string][] = [
  ['', 'Match how often (default)'],
  ['today', 'Today'],
  ['this_week', 'This week'],
  ['this_month', 'This month'],
  ['last_7d', 'Last 7 days'],
  ['last_30d', 'Last 30 days'],
  ['this_quarter', 'This quarter'],
  ['last_6_months', 'Last 6 months'],
  ['ytd', 'Year to date'],
  ['all_time', 'All time'],
]
const PERIOD_LABEL: Record<string, string> = Object.fromEntries(PERIOD_OPTS.filter(([v]) => v))
const ST_GLYPH: Record<string, string> = { met: '✓', at_risk: '⚠', breached: '●', no_data: '—' }
const pct = (s: number | null | undefined): string => (s == null ? '—' : `${(s * 100).toFixed(2)}%`)

// SLA target is stored as a 0..1 fraction; the UI shows it as a percent. Default
// mirrors the backend SLA_DEFAULT_TARGET (99.9%).
const DEFAULT_TARGET = 0.999
const targetToPctStr = (t: number | null): string =>
  ((t == null ? DEFAULT_TARGET : t) * 100).toFixed(3).replace(/\.?0+$/, '')
const pctStrToTarget = (raw: string): number | null => {
  const n = parseFloat(String(raw).replace('%', '').trim())
  if (!Number.isFinite(n) || n <= 0 || n > 100) return null
  return Math.round((n / 100) * 1e5) / 1e5
}

/** One host row in an SLA report. */
export interface SLAHost {
  host_id: string | number
  host_name: string
  /** Server-rendered fallbacks ("2 days 18 hours" / "none"). The seconds below
   *  win when present — same numbers, formatted by date-fns in the browser. */
  downtime: string
  uptime?: string
  downtime_seconds?: number
  uptime_seconds?: number
  sla: number | null
  status: string
}

/** A computed SLA report payload (`GET /sla/report`). */
export interface SLAReport {
  missed: number
  overall: number | null
  period_label: string
  weekday_only?: boolean
  /** Set when any host's numbers include imported pre-install SLA history
   *  (docs/sla-history-import.md); printed under the summary as-is. */
  history_note?: string | null
  client_name: string
  host_count: number
  hosts?: SLAHost[]
  /** Permission-resolution state: super_admin | ok | no_access | unresolved.
   *  "unresolved" means the monitoring environment was unreachable — an empty
   *  host list is a failure, not a clean report. */
  perms_state?: string
}

/** One host group option (`GET /sla/host-groups`). */
export interface HostGroup {
  group_id: string | number
  name: string
}

/** A saved SLA report schedule (`GET /sla/schedules`). */
export interface Schedule {
  id: string | number
  name: string
  cadences: string[]
  enabled: boolean
  recipients: string[]
  mine: boolean
  created_by: string
  group_ids?: (string | number)[]
  os?: string
  /** Local hour of day (0-23) the report sends at; 23 = end of day. */
  send_hour?: number
  /** IANA timezone of send_hour (the creator's profile timezone; '' = UTC). */
  timezone?: string
  /** What the report covers (PERIOD_OPTS key); '' = match the cadence. */
  period?: string
  /** What the email carries: 'csv' | 'pdf' | 'both'. Absent on a pre-upgrade
   *  backend — the form then shows the CSV-only default it has always sent. */
  attachments?: string
}

/** Form payload submitted when creating/editing a schedule. */
export interface ScheduleFormData {
  name: string
  os: string
  group_ids: (string | number)[]
  cadences: string[]
  recipients: string[]
  enabled: boolean
  send_hour: number
  period: string
  /** 'csv' | 'pdf' | 'both' — what rides along with the emailed report. */
  attachments: string
}

/** SLA settings (`GET/PATCH /sla/settings`): the breach threshold (target uptime)
 *  plus the weekend / maintenance exclusions. Both surfaces write the same
 *  per-client row. */
export interface SlaSettingsData {
  exclude_weekends: boolean
  exclude_maintenance: boolean
  sla_target: number | null
  enabled: boolean
}

/** Result of firing a schedule immediately (`POST /sla/schedules/{id}/send-now`). */
export interface SendNowResult {
  ok: boolean
  recipients?: string[]
  error?: string
}

/** The calling user's SLA capabilities (`GET /sla/me`). Schedules and Settings
 *  are client-wide config — only an admin sees those sub-tabs; everyone else
 *  gets Latest report only. */
export interface SlaMe {
  username: string
  is_admin: boolean
  perms_state: string
}

/**
 * The per-host data/auth seam. Each host builds one of these over its own
 * transport (the widget's header-auth fetch, the SPA's bearer-JWT `api`). Methods
 * return already-unwrapped data and throw an `Error` (with a user-facing message)
 * on failure.
 */
export interface SLAClient {
  getMe(): Promise<SlaMe>
  getReport(period: string, opts?: { os?: string; group?: number }): Promise<SLAReport>
  getReportCsv(period: string): Promise<Blob>
  /** The same report as a designed PDF (docs/sla-pdf.md). `theme` is the
   *  dashboard palette it wears — 'light' is paper, 'dark' matches the screen.
   *  Optional so a host on an older backend can omit it: the button hides. */
  getReportPdf?(period: string, theme: 'light' | 'dark'): Promise<Blob>
  getHostGroups(): Promise<HostGroup[]>
  listSchedules(): Promise<Schedule[]>
  createSchedule(form: ScheduleFormData): Promise<void>
  updateSchedule(id: string | number, form: ScheduleFormData): Promise<void>
  deleteSchedule(id: string | number): Promise<void>
  sendNow(id: string | number): Promise<SendNowResult>
  getSettings(): Promise<SlaSettingsData>
  updateSettings(patch: Partial<SlaSettingsData>): Promise<SlaSettingsData>
}

type AskWhyHandler = (hostName: string, periodLabel: string) => void

export interface SLAProps {
  client: SLAClient
  onAskWhy?: AskWhyHandler
  /** Which sub-tab to open first (e.g. deep-linked from `?tab=schedules`). */
  initialTab?: 'latest' | 'schedules' | 'settings'
  /** Rendered while a sub-tab is loading (e.g. the SPA's brand spinner).
   *  Defaults to a muted "Loading…" line. */
  loader?: ReactNode
}

// Trigger a browser download from an in-memory blob. Appending to document.body
// keeps it working inside the widget's shadow DOM.
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export function SLA({ client, onAskWhy, initialTab = 'latest', loader }: SLAProps) {
  const [sub, setSub] = useState(initialTab)
  // Schedules + Settings are admin-only (client-wide config). Until /sla/me
  // resolves — or if it fails — assume non-admin, so those tabs never flash
  // for users who can't use them.
  const [isAdmin, setIsAdmin] = useState(false)
  useEffect(() => {
    let cancelled = false
    client.getMe()
      .then((me) => { if (!cancelled) setIsAdmin(!!me.is_admin) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [client])
  const active = isAdmin ? sub : 'latest'
  return (
    <div className="nw-sla">
      {isAdmin && (
        <div className="nw-segment nw-sla-subtabs" role="tablist">
          <button type="button" className={`nw-segment-btn ${active === 'latest' ? 'is-active' : ''}`} onClick={() => setSub('latest')}>Latest report</button>
          <button type="button" className={`nw-segment-btn ${active === 'schedules' ? 'is-active' : ''}`} onClick={() => setSub('schedules')}>Schedules</button>
          <button type="button" className={`nw-segment-btn ${active === 'settings' ? 'is-active' : ''}`} onClick={() => setSub('settings')}>Settings</button>
        </div>
      )}
      {active === 'latest'
        ? <LatestReport client={client} onAskWhy={onAskWhy} loader={loader} />
        : active === 'schedules'
        ? <Schedules client={client} loader={loader} />
        : <SlaSettings client={client} loader={loader} />}
    </div>
  )
}

interface LatestReportProps {
  client: SLAClient
  onAskWhy?: AskWhyHandler
  loader?: ReactNode
}

function LatestReport({ client, onAskWhy, loader }: LatestReportProps) {
  const [period, setPeriod] = useState('this_month')
  const [report, setReport] = useState<SLAReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [pdfBusy, setPdfBusy] = useState(false)
  // The PDF's palette. Light is the default because a PDF is paper; "Dark"
  // is for people who want it to match the screen they read all day. It is a
  // property of the DOWNLOAD, not of the page — the page keeps its own theme.
  const [pdfDark, setPdfDark] = useState(false)
  const [query, setQuery] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true); setError(null)
    client.getReport(period)
      .then((d) => {
        if (cancelled) return
        // An empty report is only trustworthy when permissions actually resolved.
        // "unresolved" means we couldn't reach the monitoring environment — show
        // it as an error instead of a misleading "all hosts met SLA / no data".
        if (d.perms_state === 'unresolved') {
          setError("We couldn't reach your monitoring environment, so this report is unavailable. Please try again in a moment.")
          setReport(null)
        } else {
          setReport(d)
        }
        setLoading(false)
      })
      .catch((e) => { if (!cancelled) { setError((e as Error)?.message || 'Could not load report.'); setLoading(false) } })
    return () => { cancelled = true }
  }, [period, client])

  async function download() {
    setDownloading(true)
    try {
      const blob = await client.getReportCsv(period)
      downloadBlob(blob, `sla_report_${period}.csv`)
    } catch { /* surfaced by the report load; a failed download is non-fatal */ }
    finally { setDownloading(false) }
  }

  async function downloadPdf() {
    if (!client.getReportPdf) return
    setPdfBusy(true)
    try {
      const blob = await client.getReportPdf(period, pdfDark ? 'dark' : 'light')
      downloadBlob(blob, `sla_report_${period}.pdf`)
    } catch { /* same as the CSV: non-fatal, the report itself is on screen */ }
    finally { setPdfBusy(false) }
  }

  return (
    <div className="nw-sla-body">
      <div className="nw-sla-periodbar">
        {PERIODS.map(([v, l]) => (
          <button key={v} type="button" className={`nw-chip ${period === v ? 'is-active' : ''}`} onClick={() => setPeriod(v)}>{l}</button>
        ))}
        {client.getReportPdf && (
          // One split pill, two segments: the left downloads, the right flips
          // the palette the download will use — reads as "↓ PDF | Light", one
          // control for one artifact instead of two look-alike buttons.
          <span className="nw-sla-pdfpill">
            <button type="button" className="nw-sla-pdfdl" onClick={downloadPdf} disabled={pdfBusy || !report}
              title="Download this report as a PDF, in the dashboard's design">
              {pdfBusy ? 'Preparing…' : '↓ PDF'}
            </button>
            <button type="button" className={`nw-sla-pdftheme${pdfDark ? ' is-on' : ''}`}
              aria-pressed={pdfDark} onClick={() => setPdfDark((v) => !v)}
              title={pdfDark ? 'PDF uses the dark palette — click for light (paper)' : 'PDF uses the light (paper) palette — click for dark'}>
              {pdfDark ? 'Dark' : 'Light'}
            </button>
          </span>
        )}
        <button type="button" className="nw-sla-dl" onClick={download} disabled={downloading || !report}>
          {downloading ? 'Preparing…' : '↓ CSV'}
        </button>
      </div>
      {loading ? (loader ?? <div className="nw-sla-muted">Loading…</div>)
        : error ? <div className="nw-feedback-error">{error}</div>
        : report && report.perms_state === 'no_access'
        ? <EmptyState kind="lock" variant="info" title="No host access"
            body="Your account doesn't have access to any hosts, so there's no SLA report to show. Ask an administrator to grant host access." />
        : report && (
        <>
          <div className={`nw-sla-summary ${report.missed ? 'is-bad' : 'is-ok'}`}>
            <div className="nw-sla-summary-title">
              {report.missed ? `${report.missed} host${report.missed === 1 ? '' : 's'} missed SLA` : 'All hosts met SLA'}
            </div>
            <div className="nw-sla-summary-sub">
              <b>{pct(report.overall)}</b> avg uptime · {report.period_label}{report.weekday_only ? ' · weekdays only' : ''}
              {totalDowntime(report) ? <> · <b>{totalDowntime(report)}</b> total downtime</> : null}
            </div>
            {report.history_note ? <div className="nw-sla-summary-note">{report.history_note}</div> : null}
          </div>
          <div className="nw-sla-listhead">{report.client_name} · {report.host_count} host{report.host_count === 1 ? '' : 's'}</div>
          <input
            className="nw-field-input nw-sla-search"
            type="search"
            placeholder="Search hosts…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <HostList report={report} onAskWhy={onAskWhy} query={query} onClearQuery={() => setQuery('')} />
        </>
      )}
    </div>
  )
}

/** Downtime summed across every host in the report, in words. Empty when the
 *  report carries no seconds (older payload) or nothing was down. */
function totalDowntime(report: SLAReport): string {
  const secs = (report.hosts || []).reduce(
    (sum, h) => sum + (typeof h.downtime_seconds === 'number' ? h.downtime_seconds : 0), 0)
  const text = humanizeSeconds(secs)
  return text === 'none' ? '' : text
}

interface HostRowProps {
  h: SLAHost
  periodLabel: string
  onAskWhy?: AskWhyHandler
}

/** One duration cell. Percentages don't say whether 99.2% cost twenty minutes
 *  or six hours, so uptime and downtime each get their own column. The seconds
 *  win when the payload carries them (formatted in the browser, like every
 *  other duration on screen); the server's own labels are the fallback for a
 *  report that predates them. */
function duration(secs: number | undefined, label: string | undefined, sla: number | null): string {
  if (typeof secs === 'number') return humanizeSeconds(secs)
  if (label) return label
  return sla == null ? 'no data' : '—'
}

function HostRow({ h, periodLabel, onAskWhy }: HostRowProps) {
  const up = duration(h.uptime_seconds, h.uptime, h.sla)
  const down = duration(h.downtime_seconds, h.downtime, h.sla)
  return (
    <div className="nw-sla-row">
      <div className="nw-sla-main">
        <div className="nw-sla-host" title={h.host_name}>{h.host_name}</div>
        {/* Two cells, fixed-width so they line up under the header on a wide
            panel; the container query stacks them under the host name in the
            widget, where there is no room for four columns. */}
        <div className="nw-sla-times">
          <span className={`nw-sla-up${up === 'none' || up === 'no data' ? ' is-quiet' : ''}`} title={`Uptime: ${up}`}>
            <span className="nw-sla-inline-label">up </span>{up}
          </span>
          <span className={`nw-sla-down${down === 'none' || down === 'no data' ? ' is-quiet' : ''}`} title={`Downtime: ${down}`}>
            <span className="nw-sla-inline-label">down </span>{down}
          </span>
        </div>
      </div>
      <div className="nw-sla-uptime">{pct(h.sla)}</div>
      <div className={`nw-sla-status nw-st-${h.status}`} title={h.status.replace('_', ' ')}>{ST_GLYPH[h.status]}</div>
      {(h.status === 'breached' || h.status === 'at_risk') && onAskWhy
        ? <button type="button" className="nw-sla-askwhy" onClick={() => onAskWhy(h.host_name, periodLabel)}>Ask why →</button>
        : <span className="nw-sla-askwhy-spacer" />}
    </div>
  )
}

/** The column header. Same class names as a row, so the columns can only line
 *  up — a header with its own widths drifts the first time one changes. */
function HostListHead() {
  return (
    <div className="nw-sla-head" aria-hidden="true">
      <div className="nw-sla-main">
        <div className="nw-sla-host">Host</div>
        <div className="nw-sla-times">
          <span className="nw-sla-up">Uptime</span>
          <span className="nw-sla-down">Downtime</span>
        </div>
      </div>
      <div className="nw-sla-uptime">SLA</div>
      <div className="nw-sla-status" />
      <span className="nw-sla-askwhy-spacer" />
    </div>
  )
}

interface HostListProps {
  report: SLAReport
  onAskWhy?: AskWhyHandler
  /** Case-insensitive host-name filter from the search box. */
  query?: string
  onClearQuery?: () => void
}

const GROUP_PREVIEW = 5

// Grouped by the client's SLA target: "Below target" = hosts that missed it
// (breached + at-risk), worst first; "Meeting target" = hosts at/above it (met),
// best first. Each group previews the top 5 with a "See all N" toggle. No-data
// hosts (no trend this period) are counted, not listed. The search box filters
// by host name across both groups (so you can find a host without expanding).
function HostList({ report, onAskWhy, query, onClearQuery }: HostListProps) {
  const all = report.hosts || []
  if (!all.length) {
    return <EmptyState kind="chart" variant="info" title="No SLA data in this period"
      body="Hosts appear here once monitoring collects uptime data for the selected period. Try a longer period like Year to date." />
  }
  const q = (query || '').trim().toLowerCase()
  const hosts = q ? all.filter((h) => h.host_name.toLowerCase().includes(q)) : all
  if (!hosts.length) {
    return <EmptyState kind="search" variant="filter" title={`No hosts match “${query}”`}
      body="Check the spelling, or clear the search to see every host in this report."
      action={onClearQuery && { label: 'Clear search', onClick: onClearQuery }} />
  }
  const measured = hosts.filter((h) => h.sla != null)
  const noData = hosts.length - measured.length
  const below = measured
    .filter((h) => h.status === 'breached' || h.status === 'at_risk')
    .sort((a, b) => (a.sla as number) - (b.sla as number))   // worst first
  const above = measured
    .filter((h) => h.status === 'met')
    .sort((a, b) => (b.sla as number) - (a.sla as number))   // best first
  return (
    <>
      <HostGroup title="Below target" tone="bad" hosts={below} periodLabel={report.period_label} onAskWhy={onAskWhy} />
      <HostGroup title="Meeting target" tone="ok" hosts={above} periodLabel={report.period_label} onAskWhy={onAskWhy} />
      {noData > 0 && <div className="nw-sla-muted">{noData} host{noData === 1 ? '' : 's'} with no data this period.</div>}
    </>
  )
}

interface HostGroupProps {
  title: string
  tone: 'bad' | 'ok'
  hosts: SLAHost[]
  periodLabel: string
  onAskWhy?: AskWhyHandler
}

function HostGroup({ title, tone, hosts, periodLabel, onAskWhy }: HostGroupProps) {
  const [showAll, setShowAll] = useState(false)
  if (!hosts.length) return null
  const shown = showAll ? hosts : hosts.slice(0, GROUP_PREVIEW)
  const extra = hosts.length - GROUP_PREVIEW
  return (
    <>
      <div className={`nw-sla-grouphead nw-sla-grouphead-${tone}`}>{title} · {hosts.length}</div>
      <div className="nw-sla-list">
        <HostListHead />
        {shown.map((h) => <HostRow key={h.host_id} h={h} periodLabel={periodLabel} onAskWhy={onAskWhy} />)}
      </div>
      {extra > 0 && (
        <button type="button" className="nw-sla-seeall" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show less' : `See all ${hosts.length} →`}
        </button>
      )}
    </>
  )
}

interface SchedulesProps {
  client: SLAClient
  loader?: ReactNode
}

interface Flash {
  ok: boolean
  msg: string
}

function Schedules({ client, loader }: SchedulesProps) {
  const [list, setList] = useState<Schedule[] | null>(null)
  const [groups, setGroups] = useState<HostGroup[]>([])
  const [filter, setFilter] = useState('all')   // all | mine
  const [editing, setEditing] = useState<Partial<Schedule> | null>(null)   // null | {} (new) | schedule (edit)
  const [flash, setFlash] = useState<Flash | null>(null)       // { ok, msg }

  const load = useCallback(() => {
    client.listSchedules().then((d) => setList(d)).catch(() => setList([]))
  }, [client])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    client.getHostGroups().then((d) => setGroups(d)).catch(() => {})
  }, [client])

  const shown = useMemo(() => (list || []).filter((s) => filter === 'all' || s.mine), [list, filter])

  async function save(form: ScheduleFormData) {
    if (editing && editing.id) await client.updateSchedule(editing.id, form)
    else await client.createSchedule(form)
    setEditing(null); load()
  }
  async function remove(s: Schedule) {
    if (!window.confirm(`Delete the "${s.name}" schedule?`)) return
    await client.deleteSchedule(s.id)
    load()
  }
  async function sendNow(s: Schedule) {
    setFlash({ ok: true, msg: `Sending "${s.name}"…` })
    try {
      const d = await client.sendNow(s.id)
      setFlash(d.ok ? { ok: true, msg: `Sent to ${(d.recipients || []).join(', ')}` }
                    : { ok: false, msg: `Send failed: ${d.error || 'error'}` })
    } catch (e) { setFlash({ ok: false, msg: `Send failed: ${(e as Error).message}` }) }
  }

  if (list === null) return <div className="nw-sla-body">{loader ?? <div className="nw-sla-muted">Loading…</div>}</div>
  return (
    <div className="nw-sla-body">
      <div className="nw-sla-schedbar">
        <div className="nw-segment">
          <button type="button" className={`nw-segment-btn ${filter === 'all' ? 'is-active' : ''}`} onClick={() => setFilter('all')}>All</button>
          <button type="button" className={`nw-segment-btn ${filter === 'mine' ? 'is-active' : ''}`} onClick={() => setFilter('mine')}>Mine</button>
        </div>
        <button type="button" className="nw-feedback-save" onClick={() => setEditing(editing ? null : {})}>+ New schedule</button>
      </div>
      {flash && <div className={flash.ok ? 'nw-feedback-flash' : 'nw-feedback-error'}>{flash.msg}</div>}
      {editing && <ScheduleForm groups={groups} initial={editing} onCancel={() => setEditing(null)} onSave={save} />}
      {shown.map((s) => (
        <div key={s.id} className="nw-sched-card">
          <div className="nw-sched-main">
            <div className="nw-sched-title">
              {s.name}
              {s.cadences.map((c) => <span key={c} className="nw-sched-badge">{CADENCE_LABEL[c] || c}</span>)}
              {s.period && <span className="nw-sched-badge">{(PERIOD_LABEL[s.period] || s.period).toUpperCase()}</span>}
              {!s.enabled && <span className="nw-sched-badge nw-sched-paused">PAUSED</span>}
            </div>
            <div className="nw-sched-sub">
              Sent to {s.recipients.join(', ') || '—'} · at {String(s.send_hour ?? 23).padStart(2, '0')}:00{s.timezone ? ` ${s.timezone}` : ''} · by {s.mine ? 'You' : s.created_by}
              {' · '}{ATTACHMENT_SUB[s.attachments || 'csv'] ?? ATTACHMENT_SUB.csv}
            </div>
          </div>
          <div className="nw-sched-actions">
            <button type="button" className="nw-sched-btn" title="Send now" onClick={() => sendNow(s)}>▶</button>
            <button type="button" className="nw-sched-btn" title="Edit" onClick={() => setEditing(s)}>✎</button>
            <button type="button" className="nw-sched-btn" title="Delete" onClick={() => remove(s)}>🗑</button>
          </div>
        </div>
      ))}
      {shown.length === 0 && !editing && (
        (list || []).length > 0
          // Schedules exist but the "Mine" filter hides them — that's a filter
          // miss, not a true empty.
          ? <EmptyState kind="search" variant="filter" title="You haven't created any schedules yet"
              body="Switch to All to see your team's schedules, or create your own."
              action={{ label: 'Show all schedules', onClick: () => setFilter('all') }} />
          : <EmptyState kind="schedules" variant="actionable" title="Schedule your first report"
              body="Get this SLA report emailed to you or your team automatically — daily, weekly, monthly, or on a custom cadence."
              action={{ label: 'New schedule', onClick: () => setEditing({}) }} />
      )}
    </div>
  )
}

interface ScheduleFormProps {
  groups: HostGroup[]
  initial: Partial<Schedule>
  onCancel: () => void
  onSave: (form: ScheduleFormData) => Promise<void>
}

/** Token-styled dropdown (same combo pattern as the group picker) — native
 *  `<select>` popups can't be brand-styled, so the highlight stays accent-blue
 *  in both themes. */
function DropSelect({ value, options, onChange }: {
  value: string
  options: [string, string][]
  onChange: (v: string) => void
}) {
  const [open, setOpen] = useState(false)
  const label = options.find(([v]) => v === value)?.[1] ?? value
  // Outside-press close on top of the blur-timeout (which still covers
  // keyboard tab-away): presses that don't move focus close instantly.
  const comboRef = useRef<HTMLDivElement>(null)
  useOutsidePress(comboRef, () => setOpen(false), open)
  return (
    <div className="nw-combo" ref={comboRef}>
      <button type="button" className="nw-field-input nw-combo-input nw-dropselect"
        aria-haspopup="listbox" aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}>
        {label}
      </button>
      <span className="nw-combo-chevron">▾</span>
      {open && (
        <div className="nw-combo-list" role="listbox">
          {options.map(([v, l]) => (
            <button key={v} type="button" role="option" aria-selected={v === value}
              className={`nw-combo-item ${v === value ? 'is-active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(v); setOpen(false) }}>{l}</button>
          ))}
        </div>
      )}
    </div>
  )
}

// Recipients editor: a summary button that opens a small dialog with an add
// input (Enter / comma / semicolon add) and per-address remove rows. Mirrors
// the dashboard's alert-recipients dialog; nw-themed so it renders inside the
// widget's shadow DOM too. Nothing leaves until the parent's `recipients`
// state changes — the dialog edits a live list held by the form.
function RecipientsField({ value, onChange }: {
  value: string[]
  onChange: (next: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [err, setErr] = useState('')
  const dlgRef = useRef<HTMLDivElement>(null)
  useOutsidePress(dlgRef, () => setOpen(false), open)

  const split = (raw: string) => raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean)
  function add() {
    const parts = split(draft)
    if (!parts.length) return
    const bad = parts.filter((p) => !p.includes('@'))
    if (bad.length) { setErr(`Not an email address: ${bad.join(', ')}`); return }
    onChange([...value, ...parts.filter((p) => !value.includes(p))])
    setDraft(''); setErr('')
  }
  const remove = (e: string) => onChange(value.filter((x) => x !== e))
  const summary = value.length === 0 ? 'No recipients yet'
    : value.slice(0, 2).join(', ') + (value.length > 2 ? ` +${value.length - 2} more` : '')

  return (
    <>
      <button type="button" className="nw-field-input nw-recip-btn" onClick={() => setOpen(true)}>
        <span className={value.length ? '' : 'nw-recip-empty'}>{summary}</span>
        <span className="nw-recip-manage">{value.length ? 'Manage' : 'Add'} →</span>
      </button>
      {open && (
        <div className="nw-dlg-overlay">
          <div className="nw-dlg" ref={dlgRef} role="dialog" aria-modal="true" aria-label="Recipients">
            <div className="nw-dlg-title">Recipients</div>
            <div className="nw-dlg-sub">Everyone below receives this scheduled report.</div>
            {value.length > 0 ? (
              <div className="nw-dlg-list">
                {value.map((e) => (
                  <div key={e} className="nw-dlg-row">
                    <span className="nw-dlg-email">{e}</span>
                    <button type="button" className="nw-dlg-remove" aria-label={`Remove ${e}`} onClick={() => remove(e)}>✕</button>
                  </div>
                ))}
              </div>
            ) : <div className="nw-dlg-empty">No recipients yet — add one below.</div>}
            <div className="nw-dlg-addrow">
              <input className="nw-field-input" value={draft} placeholder="name@example.com"
                autoFocus
                onChange={(e) => { setDraft(e.target.value); setErr('') }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }} />
              <button type="button" className="nw-sched-btn-text" onClick={add}>Add</button>
            </div>
            {err && <div className="nw-feedback-error">{err}</div>}
            <div className="nw-dlg-actions">
              <button type="button" className="nw-feedback-save" onClick={() => { add(); setOpen(false) }}>Done</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function ScheduleForm({ groups, initial, onCancel, onSave }: ScheduleFormProps) {
  const [groupIds, setGroupIds] = useState<(string | number)[]>(initial.group_ids || [])
  const [groupQuery, setGroupQuery] = useState('')
  const [groupOpen, setGroupOpen] = useState(false)
  const [os, setOs] = useState(initial.os || 'all')
  const [cadences, setCadences] = useState<string[]>(initial.cadences && initial.cadences.length ? initial.cadences : ['monthly'])
  const [sendHour, setSendHour] = useState<number>(initial.send_hour ?? 23)
  const [period, setPeriod] = useState<string>(initial.period || '')
  const [attachments, setAttachments] = useState<string>(initial.attachments || 'csv')
  const [recipients, setRecipients] = useState<string[]>(initial.recipients || [])
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // Same layering as DropSelect: blur-timeout for tab-away, outside-press for
  // pointer dismissal.
  const groupComboRef = useRef<HTMLDivElement>(null)
  useOutsidePress(groupComboRef, () => setGroupOpen(false), groupOpen)

  const nameOf = (id: string | number) => groups.find((g) => g.group_id === id)?.name || `Group ${id}`
  const filtered = groups
    .filter((g) => !groupIds.includes(g.group_id) && g.name.toLowerCase().includes(groupQuery.toLowerCase()))
    .slice(0, 40)
  const addGroup = (id: string | number) => { setGroupIds((ids) => [...ids, id]); setGroupQuery('') }
  const removeGroup = (id: string | number) => setGroupIds((ids) => ids.filter((x) => x !== id))
  const scopeName = groupIds.length === 0 ? 'All hosts'
    : groupIds.length === 1 ? nameOf(groupIds[0]) : `${groupIds.length} groups`

  const toggleCadence = (c: string) => setCadences((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]))

  async function submit() {
    setErr(null); setSaving(true)
    try {
      await onSave({
        name: scopeName, os, group_ids: groupIds, cadences, send_hour: sendHour, period,
        attachments, recipients, enabled: true,
      })
    } catch (e) { setErr((e as Error).message) } finally { setSaving(false) }
  }

  return (
    <div className="nw-sched-form">
      <label className="nw-sla-fieldlabel">Scope (host groups)</label>
      {groupIds.length > 0 && (
        <div className="nw-chip-row">
          {groupIds.map((id) => (
            <span key={id} className="nw-selchip">{nameOf(id)}
              <button type="button" aria-label="Remove" onClick={() => removeGroup(id)}>✕</button>
            </span>
          ))}
        </div>
      )}
      <div className="nw-combo" ref={groupComboRef}>
        <input className="nw-field-input nw-combo-input" value={groupQuery}
          placeholder={groupIds.length ? 'Add another group…' : 'All hosts — type to filter…'}
          onFocus={() => setGroupOpen(true)}
          onBlur={() => setTimeout(() => setGroupOpen(false), 150)}
          onChange={(e) => { setGroupQuery(e.target.value); setGroupOpen(true) }} />
        <span className="nw-combo-chevron">▾</span>
        {groupOpen && (
          <div className="nw-combo-list">
            {filtered.map((g) => (
              <button key={g.group_id} type="button" className="nw-combo-item"
                onMouseDown={(e) => { e.preventDefault(); addGroup(g.group_id) }}>{g.name}</button>
            ))}
            {!filtered.length && <div className="nw-combo-empty">{groups.length ? 'No more matching groups' : 'No groups available'}</div>}
          </div>
        )}
      </div>
      <div className="nw-sla-hint">Leave empty for all hosts in your scope.</div>

      <label className="nw-sla-fieldlabel">OS</label>
      <div className="nw-segment">
        {OS_OPTS.map(([v, l]) => <button key={v} type="button" className={`nw-segment-btn ${os === v ? 'is-active' : ''}`} onClick={() => setOs(v)}>{l}</button>)}
      </div>

      <label className="nw-sla-fieldlabel">How often (pick one or more)</label>
      <div className="nw-cadence-row">
        {CADENCES.map(([v, l]) => (
          <button key={v} type="button" title={CADENCE_LABEL[v]} className={`nw-cadence-chip ${cadences.includes(v) ? 'is-active' : ''}`} onClick={() => toggleCadence(v)}>{l}</button>
        ))}
      </div>

      <label className="nw-sla-fieldlabel">Report covers</label>
      <DropSelect value={period} options={PERIOD_OPTS} onChange={setPeriod} />

      <label className="nw-sla-fieldlabel">Send at</label>
      <DropSelect value={String(sendHour)} onChange={(v) => setSendHour(Number(v))}
        options={SEND_HOURS.map((h) => [String(h), `${String(h).padStart(2, '0')}:00${h === 23 ? ' — end of day' : ''}`] as [string, string])} />
      <div className="nw-sla-hint">In your profile timezone{initial.timezone ? ` (${initial.timezone})` : ''}.</div>

      <label className="nw-sla-fieldlabel">Attach</label>
      <div className="nw-segment">
        {ATTACHMENT_OPTS.map(([v, l]) => (
          <button key={v} type="button" className={`nw-segment-btn ${attachments === v ? 'is-active' : ''}`}
            onClick={() => setAttachments(v)}>{l}</button>
        ))}
      </div>
      <div className="nw-sla-hint">{ATTACHMENT_HINT[attachments] ?? ATTACHMENT_HINT.csv}</div>

      <label className="nw-sla-fieldlabel">Send to</label>
      <RecipientsField value={recipients} onChange={setRecipients} />
      <div className="nw-sla-hint">Add as many addresses as you need.</div>

      {err && <div className="nw-feedback-error">{err}</div>}
      <div className="nw-sched-formbtns">
        <button type="button" className="nw-feedback-save" disabled={saving || !cadences.length} onClick={submit}>{saving ? 'Saving…' : 'Save schedule'}</button>
        <button type="button" className="nw-sched-btn-text" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

interface SettingsProps {
  client: SLAClient
  loader?: ReactNode
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error'

function SlaSettings({ client, loader }: SettingsProps) {
  const [target, setTarget] = useState('99.9%')
  const [weekends, setWeekends] = useState(false)
  const [maintenance, setMaintenance] = useState(true)
  const [loading, setLoading] = useState(true)
  const [save, setSave] = useState<SaveState>('idle')

  useEffect(() => {
    let cancelled = false
    client.getSettings()
      .then((d) => {
        if (cancelled) return
        setTarget(targetToPctStr(d.sla_target) + '%')
        setWeekends(d.exclude_weekends)
        setMaintenance(d.exclude_maintenance)
        setLoading(false)
      })
      .catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [client])

  async function persist(patch: Partial<SlaSettingsData>) {
    setSave('saving')
    try {
      const d = await client.updateSettings(patch)
      setTarget(targetToPctStr(d.sla_target) + '%')
      setWeekends(d.exclude_weekends)
      setMaintenance(d.exclude_maintenance)
      setSave('saved')
      setTimeout(() => setSave((s) => (s === 'saved' ? 'idle' : s)), 1500)
    } catch {
      setSave('error')
    }
  }

  function commitTarget() {
    const t = pctStrToTarget(target)
    if (t == null) { setSave('error'); return }
    void persist({ sla_target: t })
  }

  if (loading) return <div className="nw-sla-body">{loader ?? <div className="nw-sla-muted">Loading…</div>}</div>

  return (
    <div className="nw-sla-body">
      <label className="nw-sla-fieldlabel">Target uptime</label>
      <input
        className="nw-field-input"
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        onBlur={commitTarget}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
      />
      <div className="nw-sla-hint">Hosts below this are flagged as breached. Default 99.9%.</div>

      <label className="nw-sla-fieldlabel">Exclude weekends</label>
      <div className="nw-segment">
        <button type="button" className={`nw-segment-btn ${weekends ? 'is-active' : ''}`}
          onClick={() => { setWeekends(true); void persist({ exclude_weekends: true }) }}>On</button>
        <button type="button" className={`nw-segment-btn ${!weekends ? 'is-active' : ''}`}
          onClick={() => { setWeekends(false); void persist({ exclude_weekends: false }) }}>Off</button>
      </div>
      <div className="nw-sla-hint">Only count Mon–Fri toward SLA.</div>

      <label className="nw-sla-fieldlabel">Exclude maintenance windows</label>
      <div className="nw-segment">
        <button type="button" className={`nw-segment-btn ${maintenance ? 'is-active' : ''}`}
          onClick={() => { setMaintenance(true); void persist({ exclude_maintenance: true }) }}>On</button>
        <button type="button" className={`nw-segment-btn ${!maintenance ? 'is-active' : ''}`}
          onClick={() => { setMaintenance(false); void persist({ exclude_maintenance: false }) }}>Off</button>
      </div>
      <div className="nw-sla-hint">Don't count scheduled maintenance toward SLA.</div>

      {save !== 'idle' && (
        <div className={save === 'error' ? 'nw-feedback-error' : 'nw-feedback-flash'}>
          {save === 'saving' ? 'Saving…' : save === 'saved' ? 'Saved' : "Couldn't save"}
        </div>
      )}
    </div>
  )
}
